import PlusButton from '@/components/PlusButton';
import { style } from '@/styles/globals';
import PersonIcon from '@mui/icons-material/Person';
import {
  Stack, SwipeableDrawer,
  SwipeableDrawerProps,
  Typography,
} from '@mui/material';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import ErrorIcon from '@mui/icons-material/Error';
import styled from '@mui/styled-engine';
import Box from '@mui/system/Box';
import { useMemo, useState } from 'react';
import { TAdjustPassengerParams } from '../../../slices/area/slices/searchFlightFormSlice';
import type {
  IPassengerSearchForm,
  PassengerType,
} from '../../../slices/passenger/slices/type.d';
import TwoButton from '@/components/TwoButton';
import { pr } from '@/utils/pxToRem';
import { MAX_PEOPLE_PER_BOOKING } from '@/utils/constants';
import {
  Caption,
  Headline,
} from '../index';
import Image from '../Image';
import { ILangObj } from '@/slices/goyoloSeo/sliceType';

const tripDateIcon = 'https://storage.googleapis.com/goyolo-uat/images/SeoVexImprove/people.svg';

const StyledBox = styled(Box)({
  background: style.color.black7,
  borderRadius: pr`6px`,
  display: 'flex',
  overflow: 'hidden',
  alignItems: 'flex-end',
  marginTop: pr`8px`,
  transitionDelay: '0.1s',
  '&:hover': {
    background: '#EEF2F4',
  },
});

const SelectStyledBox = styled(StyledBox)({
  // paddingTop: '10px',
  boxSizing: 'border-box',
  height: 46,
  alignItems: 'center',
});

const StyledMenuBoxContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
});
const StyledMenuBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  flexBasis: '50%',
});
const StyledSelect = styled(Select)`
  div.MuiSelect-select.Mui-disabled {
    -webkit-text-fill-color: ${style.color.black2} !important;
  }
`;

const Container = styled('div')`
  display: flex;
  flex-direction: row;

  .icon-container {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 20px 16px;
  }

  .departure-date-select {
    display: flex;
    flex: 1;
    flex-direction: column;
    height: 64px;
    justify-content: center;
  }
`;

const StyleDiv = styled('div')`
  display: flex;
  flex-direction: column;
  height: 64px;
  justify-content: center;
  height: 56px;
`;

const CustomNumericTextField = ({
  type,
  value,
  adjustPassenger,
  isDisable,
}: {
  type: PassengerType;
  value: number;
  adjustPassenger: (params: TAdjustPassengerParams) => void;
  isDisable: boolean;
}) => {
  const handleIncreaseValue = () => {
    adjustPassenger({
      passengerType: type,
      value: value + 1,
    });
  };
  const handleDecreaseValue = () => {
    if (value === 0) return;
    if (value === 1 && type === 'adt') return;
    adjustPassenger({
      passengerType: type,
      value: value - 1,
    });
  };
  const shouldDisableMinusBtn = useMemo(() => {
    if (type === 'adt') {
      if (value === 1) return true;
    }
    if (value === 0) return true;
    return false;
  }, [type, value]);

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={pr`16px`}
    >
      <PlusButton
        type="minus"
        isDisable={shouldDisableMinusBtn}
        onClick={handleDecreaseValue}
      />
      <Typography
        variant="body2"
        color={style.color.black1}
        fontWeight="500"
      >
        {value}

      </Typography>
      <PlusButton
        type="plus"
        isDisable={isDisable}
        onClick={handleIncreaseValue}
      />
    </Stack>
  );
};

const MenuItemLayout = ({
  label,
  caption,
  type,
  warning,
  passenger,
  adjustPassenger,
  shouldWarning,
  isDisable,
}: {
  label: string;
  caption: string;
  type: PassengerType;
  warning: string;
  passenger: IPassengerSearchForm;
  adjustPassenger: (params: TAdjustPassengerParams) => void;
  shouldWarning: boolean;
  isDisable: boolean;
}) => {
  const disabled = useMemo(() => {
    if (isDisable) return true;
    if (shouldWarning) return true;
    return false;
  }, [isDisable, shouldWarning]);
  return (
    <div>
      <StyledMenuBoxContainer>
        <Stack>
          <StyledMenuBox style={{ justifyContent: 'start' }}>
            <Typography
              variant="body2"
              fontWeight="bold"
            >
              {label}
            </Typography>
          </StyledMenuBox>
          <StyledMenuBox style={{ justifyContent: 'center' }}>
            <Typography variant="caption">{caption}</Typography>
          </StyledMenuBox>

        </Stack>
        <StyledMenuBox style={{ justifyContent: 'flex-end' }}>
          <CustomNumericTextField
            type={type}
            isDisable={disabled}
            value={passenger[type]}
            adjustPassenger={adjustPassenger}
          />
        </StyledMenuBox>
      </StyledMenuBoxContainer>

      <Stack
        direction="row"
        alignItems="center"
        spacing="0.2em"
        sx={{
          display: shouldWarning ? 'flex' : 'none',
        }}
      >
        <ErrorIcon
          sx={{
            width: '14px',
            height: '14px',
          }}
          color="error"
        />
        <Typography
          variant="caption"
          color="error"
        >
          {warning}
        </Typography>
      </Stack>
    </div>
  );
};
const BottomDrawer = ({ children, ...props }: SwipeableDrawerProps) => (
  <SwipeableDrawer
    {...props}
    anchor="bottom"
    sx={{
      fontFamily: 'SVN-Circular, Roboto',
    }}
    PaperProps={{
      ...props.PaperProps,
      style: {
        ...props.PaperProps?.style,
        width: '100%',
        margin: 0,
        position: 'absolute',
        left: 0,
        bottom: 0,
        background: 'FFFFFF',
        boxShadow:
          '0px 0px 0px 1px rgba(28, 59, 84, 0.05), 0px -2px 6px -2px rgba(28, 59, 84, 0.04), 0px -8px 12px -3px rgba(28, 59, 84, 0.08)',
        borderRadius: '16px 16px 0px 0px',
      },
    }}
  >
    {children}
  </SwipeableDrawer>
);

const defaultWarningObj = {
  shouldChildWarning: false,
  shouldInfantWarning: false,
  shouldAdultWarning: false,
};
const ChoosePassenger = ({
  passenger,
  adjustPassenger,
  className,
  lang,
  switchUpdateSecondTime,
}: {
  passenger: IPassengerSearchForm;
  adjustPassenger: (params: IPassengerSearchForm) => void;
  className?: any,
  switchUpdateSecondTime?: any,
  lang?: ILangObj,
}) => {
  const [openPassengerDialog, setOpenPassengerDialog] = useState(false);
  // eslint-disable-next-line max-len
  const [tempPassenger, setTempPassenger] = useState<IPassengerSearchForm>(passenger);
  const [warningObj, setWarningObj] = useState(defaultWarningObj);

  const tempAdjustPassenger = ({
    value,
    passengerType: type,
  }: TAdjustPassengerParams) => {
    let { shouldAdultWarning, shouldChildWarning, shouldInfantWarning } = warningObj;
    let { adt, chd, inf } = tempPassenger;

    switch (type) {
      case 'adt':
        if (value < adt) {
          if (chd > 2 && chd > 2 * value) {
            const diffAdt = adt - value;
            chd = Math.round(chd / (diffAdt * 2));
          }
          if (inf > 1 && inf > value) {
            inf = value;
          }
        }
        adt = value;
        shouldChildWarning = chd > 2 * adt;
        shouldInfantWarning = inf > adt;
        break;
      case 'chd':
        shouldChildWarning = value > 2 * adt;
        chd = value;
        break;
      case 'inf':
        shouldInfantWarning = value > adt;
        inf = value;
        break;
      default:
        break;
    }

    shouldAdultWarning = adt + chd + inf > MAX_PEOPLE_PER_BOOKING;
    setWarningObj({
      shouldAdultWarning,
      shouldChildWarning,
      shouldInfantWarning,
    });
    if (
      (shouldChildWarning && type === 'chd')
      || (shouldInfantWarning && type === 'inf')
      || shouldAdultWarning
    ) {
      return;
    }
    setTempPassenger({
      adt,
      chd,
      inf,
      [type]: value,
    });
  };

  const totalPassenger = passenger.adt + passenger.chd + passenger.inf;
  return (
    <Container className={`date-input-container ${className}`}>
      <div className="icon-container">
        <Image
          imageSrc={tripDateIcon}
          alt="trip-date-icon"
          className="trip-date-icon"
          imageProps={{
            width: 24,
            height: 24,
          }}
        />
      </div>
      <StyleDiv onClick={(e) => {
        e.stopPropagation();
        setOpenPassengerDialog(true);
        if (switchUpdateSecondTime) {
          switchUpdateSecondTime();
        }
      }}
      >
        <Caption className="color--medium-sub">
          {lang?.SearchBar?.flightsTab?.passenger?.passenger || 'Hành khách'}
        </Caption>
        <Headline
          className="color--darkness"
        >
          {totalPassenger}
          {' '}
          {lang?.SearchBar?.flightsTab?.passenger?.passenger || 'Hành khách'}
        </Headline>
      </StyleDiv>
      <BottomDrawer
        onOpen={() => {}}
        onClose={() => {
          setOpenPassengerDialog(false);
          setTempPassenger(passenger);
          setWarningObj(defaultWarningObj);
        }}
        open={openPassengerDialog}
      >
        <Stack
          spacing="1.75em"
          sx={{ p: '1.5em 1em' }}
        >
          <Stack
            spacing={pr`16px`}
          >
            <Stack>
              <Typography variant="h6">
                {lang?.SearchBar?.flightsTab?.passenger?.passenger || 'Hành khách'}
              </Typography>
            </Stack>
            <Stack
              spacing="1.5em"
            >
              <MenuItemLayout
                label={lang?.SearchBar?.flightsTab?.passenger?.adult || 'Người lớn'}
                type="adt"
                caption={lang?.SearchBar?.flightsTab?.passenger?.adultDesc || 'Từ đúng 12 tuổi trở lên vào ngày khởi hành'}
                warning={`${lang?.SearchBar?.flightsTab?.passenger?.warningNumberPassenger?.[0] || 'Bạn chỉ có thể đặt'} ${MAX_PEOPLE_PER_BOOKING} ${lang?.SearchBar?.flightsTab?.passenger?.warningNumberPassenger?.[1] || 'hành khách cùng lúc'}`}
                passenger={tempPassenger}
                adjustPassenger={tempAdjustPassenger}
                shouldWarning={warningObj.shouldAdultWarning}
                isDisable={warningObj.shouldAdultWarning}
              />
              <MenuItemLayout
                label={lang?.SearchBar?.flightsTab?.passenger?.child || 'Trẻ em'}
                type="chd"
                caption={lang?.SearchBar?.flightsTab?.passenger?.childDesc || 'Từ đúng 2 tuổi đến dưới 11 tuổi'}
                warning={lang?.SearchBar?.flightsTab?.passenger?.childNoti || 'Số trẻ em không được quá 2 lần số người lớn'}
                passenger={tempPassenger}
                adjustPassenger={tempAdjustPassenger}
                shouldWarning={warningObj.shouldChildWarning}
                isDisable={warningObj.shouldAdultWarning}

              />
              <MenuItemLayout
                label={lang?.SearchBar?.flightsTab?.passenger?.infant || 'Em bé'}
                type="inf"
                caption={lang?.SearchBar?.flightsTab?.passenger?.infantDesc || 'Từ đúng 14 ngày đến dưới 2 tuổi'}
                warning={lang?.SearchBar?.flightsTab?.passenger?.noti || 'Mỗi người lớn chỉ được đi kèm 1 em bé'}
                passenger={tempPassenger}
                adjustPassenger={tempAdjustPassenger}
                shouldWarning={warningObj.shouldInfantWarning}
                isDisable={warningObj.shouldAdultWarning}
              />
            </Stack>
          </Stack>
          <TwoButton
            leftTitle={lang?.global?.cancel || 'Huỷ'}
            leftVariant="contained"
            rightVariant="contained"
            rightTitle={lang?.global?.apply || 'Áp dụng'}
            onLeftClick={() => {
              setOpenPassengerDialog(false);
              setTempPassenger(passenger);
              setWarningObj(defaultWarningObj);
            }}
            onRightClick={() => {
              setOpenPassengerDialog(false);
              adjustPassenger(tempPassenger);
              setWarningObj(defaultWarningObj);
            }}
          />

        </Stack>
      </BottomDrawer>
    </Container>
  );
};

export default ChoosePassenger;
